// Device helper utilities

import { DEVICE_TYPES } from '../config/constants';

/**
 * Extract LAN IP from device object
 * Handles regular CARO devices, Hexnode-managed tablets, and UniFi network devices
 *
 * Regular devices: config.identifier.lanIP
 * Hexnode devices: config.hexnodeInfo.ipAddress or hexnode_device.raw_data.device.ipaddress
 * UniFi devices: unifi_device.ip_address or unifi_device.raw_data.ip
 *
 * @param {Object} device - Device object
 * @returns {string|null} - LAN IP address or null if not found
 */
export const getDeviceLanIP = (device) => {
  if (!device) return null;

  const deviceTypeId = device?.deviceType_id || device?.type_id || device?.deviceType;

  // Hexnode-managed tablets (type_id = 9)
  if (deviceTypeId === DEVICE_TYPES.ANDROID_TABLET) {
    // Try config.hexnodeInfo.ipAddress first
    const hexnodeIP = device?.config?.hexnodeInfo?.ipAddress;
    if (hexnodeIP) return hexnodeIP;

    // Try hexnode_device.raw_data.device.ipaddress
    const rawDataIP = device?.hexnode_device?.raw_data?.device?.ipaddress;
    if (rawDataIP) return rawDataIP;

    // Try config.identity.lanIP (fallback)
    const identityIP = device?.config?.identity?.lanIP;
    if (identityIP) return identityIP;

    return null;
  }

  // UniFi network devices
  if (isUniFiDevice(device)) {
    // Try unifi_device.ip_address first
    const unifiIP = device?.unifi_device?.ip_address;
    if (unifiIP) return unifiIP;

    // Try raw_data.ip
    const rawDataIP = device?.unifi_device?.raw_data?.ip;
    if (rawDataIP) return rawDataIP;

    // Try config.identity.lanIP (for config-detected UniFi devices)
    const identityIP = device?.config?.identity?.lanIP;
    if (identityIP) return identityIP;

    return null;
  }

  // Regular CARO devices
  // Try config.identifier.lanIP
  const identifierIP = device?.config?.identifier?.lanIP;
  if (identifierIP) return identifierIP;

  // Try config.identity.lanIP (alternate format)
  const identityIP = device?.config?.identity?.lanIP;
  if (identityIP) return identityIP;

  // Try direct lanIP field
  if (device?.lanIP) return device.lanIP;

  return null;
};

/**
 * Extract WAN IP from device object
 *
 * @param {Object} device - Device object
 * @returns {string|null} - WAN IP address or null if not found
 */
export const getDeviceWanIP = (device) => {
  if (!device) return null;

  // Try config.identifier.wanIP
  const identifierWanIP = device?.config?.identifier?.wanIP;
  if (identifierWanIP) return identifierWanIP;

  // Try direct wanIP field
  if (device?.wanIP) return device.wanIP;

  return null;
};

/**
 * Extract MAC address from device object
 *
 * @param {Object} device - Device object
 * @returns {string|null} - MAC address or null if not found
 */
export const getDeviceMacAddress = (device) => {
  if (!device) return null;

  const deviceTypeId = device?.deviceType_id || device?.type_id || device?.deviceType;

  // Hexnode-managed tablets
  if (deviceTypeId === DEVICE_TYPES.ANDROID_TABLET) {
    // Try config.identity.mac
    const identityMac = device?.config?.identity?.mac;
    if (identityMac) return identityMac;

    // Try hexnode_device.raw_data.device.wifi_mac
    const wifiMac = device?.hexnode_device?.raw_data?.device?.wifi_mac;
    if (wifiMac) return wifiMac;
  }

  // Regular devices
  // Try config.identifier.macAddress
  const identifierMac = device?.config?.identifier?.macAddress;
  if (identifierMac) return identifierMac;

  // Try direct macAddress field
  if (device?.macAddress) return device.macAddress;

  return null;
};

/**
 * Check if device is a Hexnode-managed tablet
 *
 * @param {Object} device - Device object
 * @returns {boolean} - True if Hexnode device
 */
export const isHexnodeDevice = (device) => {
  if (!device) return false;
  const deviceTypeId = device?.deviceType_id || device?.type_id || device?.deviceType;
  return deviceTypeId === DEVICE_TYPES.ANDROID_TABLET;
};

/**
 * Check if device is a UniFi network device
 *
 * @param {Object} device - Device object
 * @returns {boolean} - True if UniFi device
 */
export const isUniFiDevice = (device) => {
  if (!device) return false;
  const typeId = device?.deviceType_id || device?.type_id || device?.deviceType;
  // Method 1: Known UniFi device type IDs
  if ([DEVICE_TYPES.UNIFI_GATEWAY, DEVICE_TYPES.UNIFI_SWITCH,
       DEVICE_TYPES.UNIFI_ACCESS_POINT_IW, DEVICE_TYPES.UNIFI_ACCESS_POINT
  ].includes(typeId)) return true;
  // Method 2: Has linked unifi_device record
  if (device?.unifi_device) return true;
  // Method 3: Config data indicates UniFi
  if (device?.config?.unifiInfo) return true;
  if (device?.config?.type?.includes?.('UniFi')) return true;
  return false;
};

/**
 * Get UniFi device type name
 *
 * @param {Object} device - Device object
 * @returns {string|null} - Device type name or null
 */
export const getUniFiDeviceType = (device) => {
  if (!isUniFiDevice(device)) return null;

  const deviceTypeId = device?.deviceType_id || device?.type_id || device?.deviceType;
  const typeMap = {
    [DEVICE_TYPES.UNIFI_GATEWAY]: 'Gateway',
    [DEVICE_TYPES.UNIFI_SWITCH]: 'Switch',
    [DEVICE_TYPES.UNIFI_ACCESS_POINT_IW]: 'Access Point (In-Wall)',
    [DEVICE_TYPES.UNIFI_ACCESS_POINT]: 'Access Point',
  };

  // Fallback: try unifi_device.device_type or config data
  if (typeMap[deviceTypeId]) return typeMap[deviceTypeId];
  const udType = device?.unifi_device?.device_type || device?.config?.unifiInfo?.deviceType;
  if (udType) {
    const nameMap = {
      'gateway': 'Gateway', 'switch': 'Switch',
      'access_point': 'Access Point', 'dream_machine': 'Gateway',
      'next_gen_gateway': 'Gateway',
    };
    return nameMap[udType] || udType;
  }
  return 'UniFi Device';
};

/**
 * Get device actions available for Hexnode devices
 *
 * @param {Object} device - Device object
 * @returns {Array} - Array of possible action strings
 */
export const getHexnodeActions = (device) => {
  if (!isHexnodeDevice(device)) return [];
  return device?.config?.possibleActions || [];
};

/**
 * Format device name with location context
 *
 * @param {Object} device - Device object
 * @returns {string} - Formatted device name
 */
export const getDeviceDisplayName = (device) => {
  if (!device) return 'Unknown Device';

  const friendlyName = device.friendlyName || device.deviceName || device.name;
  const location = device.location?.friendlyName || device.locationName;

  if (friendlyName && location) {
    return `${friendlyName} (${location})`;
  }

  return friendlyName || `Device ${device.id}`;
};
